import { useEffect, useState } from 'react';
import { BookOpen, Flower2, Home, Leaf, LogIn, Menu, Sprout, UserRound, Users, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import type { SessionUser } from './types';
import './site-menu.css';

export default function SiteMenu() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState<SessionUser | null>(null);

  useEffect(() => {
    fetch('/api/auth/me', { credentials: 'include', cache: 'no-store' })
      .then((response) => response.ok ? response.json() : { user: null })
      .then((body) => setUser(body.user || null))
      .catch(() => setUser(null));
  }, [location.pathname]);

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const items = [
    { to: '/', label: 'Discover', description: 'Identify or search for a plant', icon: <Home size={20} /> },
    { to: '/plants', label: 'Plant library', description: 'Browse care guides', icon: <BookOpen size={20} /> },
    { to: '/propagation', label: 'How to propagate', description: 'Step-by-step propagation guides', icon: <Sprout size={20} /> },
    { to: '/community', label: 'Community', description: 'See plants shared by growers', icon: <Users size={20} /> },
    { to: '/my-plants', label: 'My plants', description: 'Your private saved collection', icon: <Flower2 size={20} /> },
    { to: '/account', label: user ? 'Account' : 'Sign in or create account', description: user ? user.email : 'Save plants and unlock your profile', icon: user ? <UserRound size={20} /> : <LogIn size={20} /> },
  ];

  return (
    <>
      <button type="button" className="site-menu-trigger" onClick={() => setOpen(true)} aria-label="Open PlantGrow menu" aria-expanded={open}>
        <Menu size={21} />
        <span>Menu</span>
      </button>

      {open && (
        <div className="site-menu-backdrop" onClick={() => setOpen(false)}>
          <aside className="site-menu-drawer" role="dialog" aria-modal="true" aria-label="PlantGrow menu" onClick={(event) => event.stopPropagation()}>
            <div className="site-menu-head">
              <Link to="/" className="site-menu-brand"><span><Leaf size={21} /></span><strong>PlantGrow</strong></Link>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close menu"><X size={21} /></button>
            </div>

            <div className="site-menu-intro">
              <span className="site-menu-kicker">Grow with confidence</span>
              <h2>Everything in one place.</h2>
              <p>Identify, learn, propagate, save, and explore plants shared by the community.</p>
            </div>

            <nav className="site-menu-links" aria-label="PlantGrow navigation">
              {items.map((item) => (
                <Link key={item.to} to={item.to} className={location.pathname === item.to ? 'active' : ''}>
                  <span className="site-menu-icon">{item.icon}</span>
                  <span><strong>{item.label}</strong><small>{item.description}</small></span>
                </Link>
              ))}
            </nav>

            {user?.plan === 'pro' && <div className="site-menu-plan"><Sprout size={16} /><span><strong>PlantGrow Pro</strong> is active on this account.</span></div>}
          </aside>
        </div>
      )}
    </>
  );
}
